import _ from "lodash";
import { createSelector } from "reselect";
import { Map } from "immutable";

import { AuthNormalisedEntities } from "@auth/models";
import {
    canPermissionSelectorFactory,
    levelPermissionSelectorFactory,
    getEntities as getAuthEntities,
} from "@auth/selectors";
import { FilesNormalisedEntities } from "@files/models";
import { getEntities as getFilesEntities } from "@files/selectors";
import { MusicNormalisedEntities } from "@music/models";
import { getEntities as getMusicEntities, makeHydrateAlbum } from "@music/selectors";
import { makeGetNextPage, makeHasMorePages } from "@orm/selectors";
import { ProgressStep } from "@ui/ProgressSteps";
import { messages } from "../messages";
import { getInitialState } from "../reducers";
import {
    Bundle,
    IBundle,
    UploadState as State,
    UploadNormalisedEntities,
} from "../models";

export interface PropsWithId {
    params: {
        id: string;
    };
}

export const getEntities = (state: State) =>
    state.upload.entities ? state.upload.entities : getInitialState().entities;

export const getPagination = (state: State) =>
    state.upload.pagination ? state.upload.pagination : null;

export const getList = (state: State) =>
    state.upload.list ? state.upload.list : [];

export const getBundles = createSelector(
    getEntities,
    (entities: UploadNormalisedEntities) => entities.bundles,
);

// Pagination selectors
export const hasMorePages = makeHasMorePages(getPagination);
export const getNextPage = makeGetNextPage(getPagination);

// Get individual entities
const getBundle = ({ upload }: State, props: PropsWithId) => {
    const id = Number(props.params.id);
    if (upload.entities && upload.entities.bundles.has(id)) {
        const bundle = upload.entities.bundles.get(id);
        if (bundle.exists) {
            return bundle;
        }
        else {
            return null;
        }
    }
    return new Bundle({ id });
};

export const makeGetBundle = () => createSelector(
    getBundle,
    getEntities,
    getFilesEntities,
    getMusicEntities,
    getAuthEntities,
    (
        item: Bundle,
        entities: UploadNormalisedEntities,
        files: FilesNormalisedEntities,
        music: MusicNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        const hydrate = makeHydrateBundle(entities, files, music, auth);
        return hydrate(item) as Bundle;
    },
);

// Get array of entities
export const getBundlesList = createSelector(
    getList,
    getBundles,
    getEntities,
    getFilesEntities,
    getMusicEntities,
    getAuthEntities,
    (
        list: number[],
        items: Map<number, Bundle>,
        entities: UploadNormalisedEntities,
        files: FilesNormalisedEntities,
        music: MusicNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        const hydrate = makeHydrateBundle(entities, files, music, auth);
        // Only return existing hydrated bundles.
        return _.reduce(list, (result, id) => {
                if (items.has(id)) {
                    result.push(items.get(id));
                }
                return result;
            }, [] as Bundle[])
            .map((item) => hydrate(item) as Bundle);
    },
);

// Hydrate
export const makeHydrateBundle =
    (
        entities: UploadNormalisedEntities,
        files: FilesNormalisedEntities,
        music: MusicNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) =>
        (e: Bundle | number): Bundle => {
            // if it's an ID, get it from the state.
            if (typeof e === "number") {
                e = entities.bundles.get(e);
            }

            if (e == null) {
                // Nothing to hydrate
                return e as Bundle;
            }

            const hydrateAlbum = makeHydrateAlbum(music, files, auth);

            const bundle = {} as IBundle;
            // The batch
            if (e.status != null &&
                typeof e.status === "string" &&
                entities.bundleStatuses.has(e.status)
            ) {
                bundle.status = entities.bundleStatuses.get(e.status);
            }
            // The metadataFile
            if (e.metadataFileId != null && files.files.has(e.metadataFileId)) {
                bundle.metadataFile = files.files.get(e.metadataFileId);
            }
            // The bundleFile
            if (e.bundleFileId != null && files.files.has(e.bundleFileId)) {
                bundle.bundleFile = files.files.get(e.bundleFileId);
            }
            // The album
            if (e.albumId != null && music.albums.has(e.albumId)) {
                bundle.album = hydrateAlbum(music.albums.get(e.albumId));
            }
            // The user
            if (e.userId != null && auth.users.has(e.userId)) {
                bundle.user = auth.users.get(e.userId);
            }

            // And the computed data
            e = e.setCast("viewUrl", e.getViewUrl());
            e = e.setCast("hydrated", true);

            return e.merge(bundle) as Bundle;
        };

// The selector to get the bundle states for the ProgressSteps component.
export const getBundleSteps = createSelector(
    getBundle,
    (bundle: Bundle) => getStepsForBundle(bundle),
);

export const getStepsForBundle = (bundle: Bundle) => {
    const steps: ProgressStep[] = [
        {
            message: messages.uploadMetadata,
            state: "active",
        },
        {
            message: messages.createAlbum,
            state: null,
        },
        {
            message: messages.uploadZip,
            state: null,
        },
        {
            message: messages.confirmation,
            state: null,
        },
        {
            message: messages.complete,
            state: null,
        },
    ];
    if (bundle != null && bundle.exists) {
        // First state always completed
        steps[0].state = "completed";
        // Create/Update album based on whether there's an album or not
        if (bundle.isStepCreateAlbum() && bundle.albumId != null) {
            steps[1].message = messages.updateAlbum;
        }
        steps[1].state = bundle.isStepCreateAlbum() ? "active"
            : (bundle.albumId != null ? "completed" : null);
        steps[2].state = bundle.isStepUploadZip() ? "active"
            : (bundle.bundleFileId != null ? "completed" : null);
        steps[3].state = bundle.isStepComplete() || bundle.statusName === "completing" ? "active"
            : (bundle.statusName === "complete" ? "completed" : null);
        steps[4].state = bundle.statusName === "complete" ? "completed" : null;

        if (bundle.statusName === "error") {
            if (bundle.bundleFileId != null) {
                steps[3].state = "danger";
            }
            else if (bundle.albumId != null) {
                steps[1].state = "danger";
            }
            else {
                steps[0].state = "danger";
            }
        }
    }
    return steps;
};

// Permissions selectors
const bundlePermissionPrefix = "wata.upload.bundle";
export const canViewBundle = canPermissionSelectorFactory(bundlePermissionPrefix, "view");
export const canCreateBundle = canPermissionSelectorFactory(bundlePermissionPrefix, "create");
export const canUpdateBundle = canPermissionSelectorFactory(bundlePermissionPrefix, "update");
export const canDeleteBundle = canPermissionSelectorFactory(bundlePermissionPrefix, "delete");
export const getViewBundleLevel = levelPermissionSelectorFactory(bundlePermissionPrefix, "view");
export const getCreateBundleLevel = levelPermissionSelectorFactory(bundlePermissionPrefix, "create");
export const getUpdateBundleLevel = levelPermissionSelectorFactory(bundlePermissionPrefix, "update");
export const getDeleteBundleLevel = levelPermissionSelectorFactory(bundlePermissionPrefix, "delete");
